
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ShortlistCandidateVariables {
  candidateId: string;
  jobId: string;
  jobTitle: string;
  candidateName: string;
}

const shortlistCandidate = async ({ candidateId, jobId, jobTitle, candidateName, userId }: ShortlistCandidateVariables & { userId: string | undefined }) => {
  if (!userId) {
    throw new Error('User must be authenticated to shortlist a candidate.');
  }

  const { data, error } = await supabase
    .from('candidate_history')
    .insert({
      candidate_id: candidateId,
      job_id: jobId,
      status: 'shortlisted_for_job', 
      notes: `Shortlisted ${candidateName} for job: "${jobTitle}" (ID: ${jobId})`,
      changed_by: userId,
    })
    .select() // Select the inserted row to confirm
    .single(); // Expect a single row back

  if (error) {
    console.error('Error shortlisting candidate:', error);
    throw new Error(error.message || 'Failed to shortlist candidate.');
  }

  return { success: true, candidateId, candidateName, jobId, jobTitle, historyEntry: data };
};

export const useShortlistCandidate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<
    { success: boolean; candidateId: string; candidateName: string; jobId: string; jobTitle: string; historyEntry: any }, 
    Error, 
    ShortlistCandidateVariables
  >({
    mutationFn: (variables) => shortlistCandidate({ ...variables, userId: user?.id }),
    onSuccess: (data) => {
      toast.success(`Candidate ${data.candidateName} shortlisted for job "${data.jobTitle}".`);
      // Invalidate queries that might display candidate history or job-related candidate lists
      queryClient.invalidateQueries({ queryKey: ['candidateHistory', data.candidateId] });
      // Potentially invalidate queries related to candidates for a specific job if such a query exists
      queryClient.invalidateQueries({ queryKey: ['jobPipeline', data.jobId] }); 
      queryClient.invalidateQueries({ queryKey: ['candidates'] }); // General candidates list if status changes impact it
    },
    onError: (error) => {
      toast.error(`Failed to shortlist: ${error.message}`);
    },
  });
};
